import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

// --- Custom Icons ---

const InstallIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Blue Phone */}
    <rect x="12" y="4" width="14" height="24" rx="2.5" stroke="#2962FF" strokeWidth="2" />
    <path d="M17 6H21" stroke="#2962FF" strokeWidth="2" strokeLinecap="round" />
    <circle cx="19" cy="24" r="1.5" fill="#2962FF" />

    {/* Yellow Download Box */}
    <rect x="2" y="10" width="12" height="12" rx="2" stroke="#FFC107" strokeWidth="2" fill="white" />
    <path d="M8 13V19M8 19L5.5 16.5M8 19L10.5 16.5" stroke="#FFC107" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ReviewIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Blue Document */}
    <path d="M6 4H20V12H26V28H6V4Z" stroke="#2962FF" strokeWidth="2" strokeLinejoin="round" />
    <path d="M20 4V12H26" stroke="#2962FF" strokeWidth="2" strokeLinejoin="round" />
    <path d="M10 12H16" stroke="#2962FF" strokeWidth="2" strokeLinecap="round" />
    <path d="M10 17H22" stroke="#2962FF" strokeWidth="2" strokeLinecap="round" />
    <path d="M10 22H16" stroke="#2962FF" strokeWidth="2" strokeLinecap="round" />

    {/* Yellow Check Circle */}
    <circle cx="22" cy="24" r="8" fill="white" stroke="#FFC107" strokeWidth="2" />
    <path d="M18 24L20.5 26.5L26 21" stroke="#FFC107" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ScreenshotIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Blue Image */}
    <rect x="6" y="8" width="20" height="16" stroke="#2962FF" strokeWidth="2" fill="none" />
    <path d="M6 20L11 15L16 20L20 16L26 22" stroke="#2962FF" strokeWidth="2" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="1.5" fill="#2962FF" />

    {/* Yellow Corners */}
    <path d="M4 8V4H8" stroke="#FFC107" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M24 4H28V8" stroke="#FFC107" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M4 24V28H8" stroke="#FFC107" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M24 28H28V24" stroke="#FFC107" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const EarnIcon = () => (
  <svg width="34" height="34" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Blue Bag Top */}
    <path d="M9 10L11 6C11.5 5 12.5 4 13 4H19C19.5 4 20.5 5 21 6L23 10" stroke="#2962FF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    
    {/* Blue Bag Tie/Neck */}
    <path d="M9 10C9 10 11 11.5 16 11.5C21 11.5 23 10 23 10" stroke="#2962FF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    
    {/* Blue Bag Body */}
    <path d="M9 10C6 11 5 14 5 18C5 24 8 28 16 28C24 28 27 24 27 18C27 14 26 11 23 10" stroke="#2962FF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    
    {/* Yellow Dollar Sign */}
    <path d="M16 15V23" stroke="#FFC107" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M13.5 16.5C13.5 15.5 15 15 16 15C17.5 15 18.5 16 18.5 17C18.5 18.5 16 19 16 19C16 19 13.5 19.5 13.5 21C13.5 22 14.5 23 16 23C17.5 23 18.5 22 18.5 21.5" stroke="#FFC107" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);


const StepIconWrapper = ({ children }: { children: React.ReactNode }) => (
  <Box 
    sx={{ 
      width: 72, 
      height: 72, 
      borderRadius: '50%', 
      backgroundColor: '#F8F9FA', // Very light grey
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      mr: 3, 
      flexShrink: 0,
      // Optional: Add a subtle shadow to make it pop like the image
      // boxShadow: '0 2px 8px rgba(0,0,0,0.03)' 
    }}
  >
    {children}
  </Box>
);

const TaskSteps = () => {
  const steps = [
    {
      label: "Step 1",
      icon: <InstallIcon />,
      content: (
        <>
          <span style={{ color: '#2962FF', fontWeight: 700, fontFamily: 'General-Sans-bold' }}>Install</span> the app
        </>
      ),
    },
    {
      label: "Step 2",
      icon: <ReviewIcon />,
      content: (
        <>
          Post a <span style={{ color: '#2962FF', fontWeight: 700, fontFamily: 'General-Sans-bold' }}>rating with comment</span> on play store
        </>
      )
    },
    {
      label: "Step 3",
      icon: <ScreenshotIcon />,
      content: (
        <>
          Come <span style={{ color: '#2962FF', fontWeight: 700, fontFamily: 'General-Sans-bold' }}>Back to this website</span> and upload the screenshot of your installation
        </>
      )
    },
    {
      label: "Step 4",
      icon: <EarnIcon />,
      content: (
        <>
          Add UPI ID and <span style={{ color: '#2962FF', fontWeight: 700, fontFamily: 'General-Sans-bold' }}>get paid</span> instantly
        </>
      )
    }
  ];

  return (
    <Box sx={{ width: "100%", py: 1, backgroundColor: "transparent" }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          textAlign: "center",
          mb: 5,
          color: "#000",
          fontFamily: 'General-Sans-bold, sans-serif',
          fontSize: '1.5rem',
          lineHeight: 1.3
        }}
      >
        Follow These Steps To
        <br />
        Complete Task:
      </Typography>

      <Stack spacing={4}>
        {steps.map((step, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
            <StepIconWrapper>
               {step.icon}
            </StepIconWrapper>
            
            <Box>
              <Typography 
                variant="caption" 
                display="block" 
                sx={{ 
                  color: '#9E9E9E', 
                  mb: 0.5, 
                  fontSize: '0.8rem', 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {step.label}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 500, 
                  fontSize: '1.05rem', 
                  color: '#000', 
                  lineHeight: 1.4,
                  maxWidth: '260px' 
                }}
              >
                {step.content}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default TaskSteps;
